/**
 * Catalog Section - Mobile Slider Initialization
 * Pattern replicated from transparent-pricing-section.ts
 */

declare global {
    interface Window {
        Swiper: any;
        catalogMobileSwiper?: any;
    }
}

export function initializeCatalogMobileSlider(containerElement: HTMLElement | null): void {
    // Only run on mobile screens
    if (window.innerWidth > 991) {
        return;
    }

    if (!containerElement) {
        return;
    }

    // Check if slider already exists
    if (containerElement.querySelector('.catalog-section__mobile-slider-new')) {
        return;
    }

    // Get the right column with catalog carousel
    const originalRightCol = containerElement.querySelector('.col-xl-6:nth-child(2)');
    if (!originalRightCol) return;

    const catalogCarousel = originalRightCol.querySelector('.catalog-section__right');
    if (!catalogCarousel) return;

    // Create Swiper container structure
    const sliderContainer = document.createElement('div');
    sliderContainer.className = 'catalog-section__mobile-slider-new';

    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'swiper-container catalog-mobile-swiper-new';

    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    // Get catalog images from window state or DOM
    const catalogImages = (window as any).catalogImages || [];
    
    if (catalogImages.length === 0) {
        return;
    }

    // Get current language from document
    const currentLang = document.documentElement.lang || 'pt';

    // Helper: get text from catalog image based on language
    const getCatalogText = (image: any): string => {
        const t = image.catalogImageText;
        if (!t) return '';
        if (typeof t === 'string') return t;
        return (currentLang === 'en' ? t.en : t.pt) || t.pt || '';
    };

    // Helper: render marked text (convert <r>...</r> to red spans) as HTML
    const renderCatalogTextHtml = (markedText: string): string => {
        if (!markedText) return '';
        return markedText.replace(/<r>(.*?)<\/r>/g, '<span style="color: #ea2127;">$1</span>');
    };

    // Create slides from catalog images
    catalogImages.forEach((image: any, index: number) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';

        const slideContent = document.createElement('div');
        slideContent.className = 'catalog-mobile-slide-item';

        // Add catalog text on top if available
        const textContent = getCatalogText(image);
        if (textContent) {
            const textEl = document.createElement('h3');
            textEl.className = 'catalog-mobile-slide-text';
            textEl.innerHTML = renderCatalogTextHtml(textContent);
            slideContent.appendChild(textEl);
        }

        const img = document.createElement('img');
        img.src = image.catalogImageUrl;
        img.alt = image.catalogImageName || `Catalog Page ${index + 1}`;
        img.loading = index < 2 ? 'eager' : 'lazy';
        img.style.cssText = 'width: 100%; cursor: pointer;';
        img.dataset.imageIndex = index.toString();

        slideContent.appendChild(img);
        slide.appendChild(slideContent);
        swiperWrapper.appendChild(slide);
    });

    swiperContainer.appendChild(swiperWrapper);

    // Add pagination
    const pagination = document.createElement('div');
    pagination.className = 'swiper-pagination';
    swiperContainer.appendChild(pagination);

    sliderContainer.appendChild(swiperContainer);

    // Insert after the section title in left column
    const leftCol = containerElement.querySelector('.catalog-section__left');
    const catalogText = leftCol?.querySelector('.catalog-section__text');
    if (catalogText && catalogText.parentElement) {
        catalogText.parentElement.insertBefore(sliderContainer, catalogText.nextSibling);
    }

    // Initialize Swiper - wait for it to be available
    const initSwiper = (): void => {
        if (typeof window.Swiper !== 'undefined') {
            const swiperElement = document.querySelector('.catalog-mobile-swiper-new');
            if (!swiperElement) return;

            // Check if already initialized
            if ((swiperElement as any).swiper) return;

            // Variables to track swipe direction
            let touchStartX = 0;
            let touchStartY = 0;

            const swiper = new window.Swiper('.catalog-mobile-swiper-new', {
                slidesPerView: 1.15,
                spaceBetween: 15,
                centeredSlides: false,
                loop: false,
                autoplay: false,
                pagination: {
                    el: '.catalog-section__mobile-slider-new .swiper-pagination',
                    clickable: true,
                },
                speed: 300,
                effect: 'slide',
                // Swiper touch settings - allow vertical scroll
                simulateTouch: true,
                touchRatio: 1,
                touchAngle: 45,
                threshold: 15,
                touchStartPreventDefault: false,
                touchMoveStopPropagation: false,
                preventClicks: false,
                preventClicksPropagation: false,
                // Custom handling for vertical vs horizontal scroll
                on: {
                    touchStart: function(_swiper: any, event: any) {
                        touchStartX = event.touches[0].clientX;
                        touchStartY = event.touches[0].clientY;
                    },
                    touchMove: function(swiper: any, event: any) {
                        if (!touchStartX || !touchStartY) return;

                        const diffX = Math.abs(event.touches[0].clientX - touchStartX);
                        const diffY = Math.abs(event.touches[0].clientY - touchStartY);

                        // If vertical movement is greater, disable slider and allow page scroll
                        if (diffY > diffX && diffY > 15) {
                            swiper.allowTouchMove = false;
                        } else if (diffX > diffY && diffX > 15) {
                            // Horizontal swipe - enable slider
                            swiper.allowTouchMove = true;
                        }
                    },
                    touchEnd: function(swiper: any) {
                        // Re-enable touch move for next interaction
                        swiper.allowTouchMove = true;
                        touchStartX = 0;
                        touchStartY = 0;
                    }
                }
            });

            window.catalogMobileSwiper = swiper;
            
            // Add click handlers to images for fullscreen view
            setupFullscreenHandlers(catalogImages);
            
            // Dispatch event that mobile slider is ready
            // Wait a tiny bit to ensure swiper is fully mounted and images are loading
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('catalogMobileSliderReady'));
            }, 100);
        } else {
            // Retry after a short delay if Swiper is not available
            setTimeout(initSwiper, 200);
        }
    };

    setTimeout(initSwiper, 200);
}

// Setup fullscreen view handlers for mobile
function setupFullscreenHandlers(catalogImages: any[]): void {
    // Only on mobile
    if (window.innerWidth > 991) {
        return;
    }

    const images = document.querySelectorAll('.catalog-mobile-slide-item img');
    
    images.forEach((img: Element) => {
        img.addEventListener('click', function() {
            const imageIndex = parseInt((img as HTMLElement).dataset.imageIndex || '0');
            openFullscreenModal(catalogImages, imageIndex);
        });
    });
}

// Open fullscreen modal
function openFullscreenModal(catalogImages: any[], startIndex: number): void {
    let currentIndex = startIndex;

    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'catalog-fullscreen-modal';
    modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.95);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: fadeIn 0.3s ease;
    `;

    // Create close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'catalog-fullscreen-close';
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
        position: absolute;
        top: 20px;
        right: 20px;
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: #fff;
        font-size: 40px;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        cursor: pointer;
        z-index: 1000000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        line-height: 1;
        padding: 0;
    `;

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.style.cssText = `
        position: relative;
        width: 95%;
        height: 90%;
        display: flex;
        align-items: center;
        justify-content: center;
    `;

    // Create image element
    const img = document.createElement('img');
    img.style.cssText = `
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 8px;
    `;
    img.src = catalogImages[currentIndex].catalogImageUrl;
    img.alt = catalogImages[currentIndex].catalogImageName || `Page ${currentIndex + 1}`;

    // Create navigation arrows (only if there are multiple images)
    if (catalogImages.length > 1) {
        // Previous button
        const prevBtn = document.createElement('button');
        prevBtn.className = 'catalog-fullscreen-prev';
        prevBtn.innerHTML = '<i class="fas fa-chevron-left"></i>';
        prevBtn.style.cssText = `
            position: absolute;
            left: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(234, 33, 39, 0.9);
            border: none;
            color: #fff;
            font-size: 24px;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            cursor: pointer;
            z-index: 1000000;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
        `;

        // Next button
        const nextBtn = document.createElement('button');
        nextBtn.className = 'catalog-fullscreen-next';
        nextBtn.innerHTML = '<i class="fas fa-chevron-right"></i>';
        nextBtn.style.cssText = `
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(234, 33, 39, 0.9);
            border: none;
            color: #fff;
            font-size: 24px;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            cursor: pointer;
            z-index: 1000000;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
        `;

        // Navigation functions
        const updateImage = () => {
            img.style.opacity = '0';
            setTimeout(() => {
                img.src = catalogImages[currentIndex].catalogImageUrl;
                img.alt = catalogImages[currentIndex].catalogImageName || `Page ${currentIndex + 1}`;
                img.style.opacity = '1';
            }, 150);
        };

        prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = currentIndex > 0 ? currentIndex - 1 : catalogImages.length - 1;
            updateImage();
        });

        nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = currentIndex < catalogImages.length - 1 ? currentIndex + 1 : 0;
            updateImage();
        });

        imageContainer.appendChild(prevBtn);
        imageContainer.appendChild(nextBtn);
    }

    // Create page indicator
    const pageIndicator = document.createElement('div');
    pageIndicator.style.cssText = `
        position: absolute;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.7);
        color: #fff;
        padding: 10px 20px;
        border-radius: 20px;
        font-size: 14px;
        z-index: 1000000;
    `;
    
    const updatePageIndicator = () => {
        pageIndicator.textContent = `${currentIndex + 1} / ${catalogImages.length}`;
    };
    updatePageIndicator();

    // Update page indicator on navigation
    if (catalogImages.length > 1) {
        const originalPrevClick = imageContainer.querySelector('.catalog-fullscreen-prev');
        const originalNextClick = imageContainer.querySelector('.catalog-fullscreen-next');
        
        if (originalPrevClick) {
            originalPrevClick.addEventListener('click', updatePageIndicator);
        }
        if (originalNextClick) {
            originalNextClick.addEventListener('click', updatePageIndicator);
        }
    }

    // Add smooth fade transition for image
    img.style.transition = 'opacity 0.3s ease';

    // Assemble modal
    imageContainer.appendChild(img);
    modal.appendChild(closeBtn);
    modal.appendChild(imageContainer);
    modal.appendChild(pageIndicator);

    // Prevent body scroll - Enhanced for mobile
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Prevent touch scroll on modal background
    const preventTouchScroll = (e: TouchEvent) => {
        // Allow scrolling on the image itself for pinch zoom
        if ((e.target as HTMLElement).tagName === 'IMG') {
            return;
        }
        // Prevent scroll on everything else
        if (e.cancelable) {
            e.preventDefault();
        }
    };
    
    modal.addEventListener('touchmove', preventTouchScroll, { passive: false });

    // Close handlers
    const closeModal = () => {
        modal.style.animation = 'fadeOut 0.3s ease';
        setTimeout(() => {
            if (modal.parentNode) {
                modal.parentNode.removeChild(modal);
            }
        }, 300);
        
        // Restore scroll position and body styles
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
        
        // Remove touch scroll prevention
        modal.removeEventListener('touchmove', preventTouchScroll);
    };

    closeBtn.addEventListener('click', closeModal);
    
    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal();
        }
    });

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            closeModal();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);

    // Add to DOM
    document.body.appendChild(modal);
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
        }
        .catalog-fullscreen-close:hover {
            background: rgba(255, 255, 255, 0.4) !important;
            transform: scale(1.1);
        }
        .catalog-fullscreen-prev:hover,
        .catalog-fullscreen-next:hover {
            background: rgba(234, 33, 39, 1) !important;
            transform: translateY(-50%) scale(1.1);
        }
    `;
    document.head.appendChild(style);
}

